import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Save, Eye } from "lucide-react";
import { toast } from "sonner";
import { PlaceholderBar } from "@/components/EmailPlaceholders";

// Admin-managed named email templates picked when sending a proposal.
export default function ProposalEmailTemplates() {
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null); // { subject, html }
  const activeRef = useRef(null);
  const load = () => api.get("/proposal-email-templates").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const upd = (i, patch) => setItems((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const add = () => setItems((xs) => [...xs, { id: `new-${Date.now()}`, name: "", subject: "", body: "" }]);
  const del = (i) => setItems((xs) => xs.filter((_, j) => j !== i));
  const save = async () => {
    try { const { data } = await api.put("/proposal-email-templates", { items }); setItems(data); toast.success("Email templates saved"); }
    catch { toast.error("Could not save templates"); }
  };
  const showPreview = async (t) => {
    try {
      const { data } = await api.post("/proposal-email-templates/preview", { subject: t.subject, body: t.body });
      setPreview(data);
    } catch { toast.error("Could not build preview"); }
  };
  return (
    <Card className="p-5 space-y-3" data-testid="proposal-email-templates">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-alt font-semibold text-cass-navy">Proposal send templates</h3>
          <p className="text-xs text-cass-slate">Named templates the sender picks when sending a proposal. Use the Insert chips to add placeholders — they auto-fill on send.</p>
        </div>
        <Button size="sm" variant="outline" onClick={add} data-testid="pet-add"><Plus className="w-4 h-4 mr-1" />Add template</Button>
      </div>
      {items.length === 0 && <p className="text-sm text-cass-slate">No templates yet. Add one to offer a choice at send.</p>}
      <div className="space-y-4">
        {items.map((t, i) => (
          <div key={t.id} className="border border-border rounded-lg p-3 space-y-2" data-testid={`pet-row-${i}`}>
            <div className="flex items-center gap-2">
              <Input value={t.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder="Template name (e.g. Standard, Renewal, Warm)" data-testid={`pet-name-${i}`} />
              <Button size="sm" variant="outline" onClick={() => showPreview(t)} data-testid={`pet-preview-${i}`}><Eye className="w-4 h-4 mr-1" />Preview</Button>
              <Button size="icon" variant="ghost" onClick={() => del(i)} data-testid={`pet-del-${i}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
            <div><Label className="text-xs">Subject</Label><Input value={t.subject} onChange={(e) => upd(i, { subject: e.target.value })} onFocus={(e) => { activeRef.current = { el: e.target, apply: (v) => upd(i, { subject: v }) }; }} data-testid={`pet-subject-${i}`} /></div>
            <div><Label className="text-xs">Email message</Label><Textarea rows={5} value={t.body} onChange={(e) => upd(i, { body: e.target.value })} onFocus={(e) => { activeRef.current = { el: e.target, apply: (v) => upd(i, { body: v }) }; }} data-testid={`pet-body-${i}`} /></div>
            <PlaceholderBar activeRef={activeRef} />
          </div>
        ))}
      </div>
      <Button className="bg-cass-navy text-white" onClick={save} data-testid="pet-save"><Save className="w-4 h-4 mr-1" />Save templates</Button>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Email preview (sample data)</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-2" data-testid="pet-preview-dialog">
              <div className="text-sm"><span className="text-cass-slate">Subject: </span><span className="font-medium text-cass-navy">{preview.subject}</span></div>
              <iframe title="Email preview" srcDoc={preview.html} className="w-full h-[60vh] rounded-lg border border-border bg-white" data-testid="pet-preview-frame" />
              <p className="text-[11px] text-cass-slate">Placeholders shown with example values (e.g. {"{client_first_name}"} → Jane). Real client details fill in on send.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
